/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Tue Jun 07 2022
*  File : SNHUBSessionFunctions.ts
*******************************************/

import { cookieExists, deleteCookie, getCookie, setCookie } from "@ciaopeople/cp-shatsu/Cookie";
import { Logger } from "@ciaopeople/cp-shatsu/Logging";
import { isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { isStorableSession, StorableSession } from "../session/StorableSession.js";
import { UserRole } from "../login/UserRole.js";
import { SNHubCookieNamesMap } from "./SNHUBConfig.js";
import { isSNUUserDataObject, SNUUserDataObject } from "./SNHUBDataTypes.js";
import { hasOptionalPropertyOfType } from "@ciaopeople/cp-shatsu/CustomTypes";
import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";

const SNU_SESSION_EXPIREY_DAYS = 365 * 10; //10 years
const SNU_VERSION_COOKIE_NAME = "snucookup";


export interface SNUSession extends StorableSession { 
    oldToken?: string,
    check?: number,
    data: SNUUserDataObject,
    role: UserRole.SNHUB_ANONYMOUS_USER
}


export function isSNUSession(obj: any): obj is SNUSession {
    return isStorableSession(obj) && 
            obj.role == UserRole.SNHUB_ANONYMOUS_USER &&
            hasOptionalPropertyOfType(obj, "oldToken", isString) &&
            hasOptionalPropertyOfType(obj, "check", isNumber) &&
            hasOwnProperty(obj, "data") &&
            isSNUUserDataObject(obj["data"]);
}

export class SNHUBSessionFunctions {
    
    constructor(
        private cookieNames: SNHubCookieNamesMap,
        private cookieDomain: string,
        private path: string = '/',
        private cookieVersion: number = null,
        private logger: Logger
    ) {

    }


    /**
     * Restituisce il la versione corrente dei cookie
     * @return la versione o null se non impostata
     */
    public getCurrentCookieVersion(): number|null {
        if (cookieExists(SNU_VERSION_COOKIE_NAME)) {
            const rval = parseInt( getCookie(SNU_VERSION_COOKIE_NAME) );

            return isNaN(rval) ? null : rval;
        }
        return null;
    }

    /**
     * Imposta il cookie di versione
     */
    public storeCurrentCookieVersion() {
        this.storeCookie(SNU_VERSION_COOKIE_NAME, ""+this.cookieVersion, 365);
    }


    /**
     * Restituisce il token di autenticazione del Simple Notification User
     * @return il token SNU | null se il token non esiste
     */
    public getToken(): string|null {
        if (this.hasToken()) {
            return getCookie(this.cookieNames.token);
        }
        return null;
    }

    /**
     * Imposta il token di autenticazione
     */
    public storeToken(tok: string) {
        this.storeCookie(this.cookieNames.token, tok);
    }

    /**
     * True se esiste un token
     */
    public hasToken(): boolean {
        return cookieExists(this.cookieNames.token);
    }

    
    /**
     * Restituisce il vecchio token di autenticazione del Simple Notification User
     * 
     * @return il token SNU | null se il token non esiste
     */
    public getOldToken() {
        if (cookieExists(this.cookieNames.oldToken)) {
            return getCookie(this.cookieNames.oldToken);
        }
        return null;
    }

    /**
     * Imposta il vecchio token di autenticazione
     */
    public storeOldToken(tok: string) {
        this.storeCookie(this.cookieNames.oldToken, tok);
    }




    /**
     * Salva i dati del prossimo check nella sessione (cookies)
     * 
     * @param nextCheck millisecondi al prossimo check
     */
    public storeNextCheck(nextCheck: number) {
        this.storeCookie(this.cookieNames.next_check, "" + (new Date().getTime() + nextCheck) );
    }




    /**
     * Recupera il timestamp del prossimo check
     * 
     * @return Timestamp del prossimo check
     */
    public getNextCheckTimestamp(): number {
        if (cookieExists(this.cookieNames.next_check)) {
            const ck = parseInt( getCookie(this.cookieNames.next_check) );
            return isNaN(ck) ? 0 : ck;
        }
        return 0;
    }



    
    /**
     * Recuprea lo user data dalla session (cookies)
     * 
     * @return Dati utente o null se la sessione non esiste
     */
    public getUserData(): SNUUserDataObject|null {
        if (cookieExists(this.cookieNames.userdata)) {
            const rawValue = getCookie(this.cookieNames.userdata);
            try {
                const ud = JSON.parse( rawValue );
                if (isObject(ud)) {
                    return ud as SNUUserDataObject;
                }
            }
            catch (ex) {
                this.logger.warn("Parsing error detected while calling SNHUBSessionFunctions::getUserData", rawValue );
            }
        }
        return null;
    }


    /**
     * Salva i dati utente nella sessione (cookies)
     * 
     * @param userData
     */
    public storeUserData(userData: SNUUserDataObject) {
        this.storeCookie(
            this.cookieNames.userdata,
            JSON.stringify(userData)
        );
    }



    /**
     * Restituisce tutti i dati di sessione.
     * 
     * @return sessione snu o null se non esiste
     */
    public getSession(): SNUSession|null {
        if (this.hasUsableSession()) {
            return {
                role: UserRole.SNHUB_ANONYMOUS_USER,
                token: this.getToken(),
                oldToken: this.getOldToken(),
                data: this.getUserData(),
                check: this.getNextCheckTimestamp()
            };
        }
        else {
            return null;
        }
    }

    /**
     * True se esiste una sessione (alias se esiste un token)
     */
    public hasUsableSession(): boolean {
        return this.hasToken() !== null;
    }

    
    /**
     * Distrugge la sessione corrente
     */
    public unsetSession() {
        [
            this.cookieNames.token,
            this.cookieNames.oldToken,
            this.cookieNames.userdata,
            this.cookieNames.next_check
        ].forEach(
            c => {
                deleteCookie(c, this.cookieDomain, this.path);
            }
        );
    }






    private storeCookie(name: string, value: string, expiration: number = SNU_SESSION_EXPIREY_DAYS) {
        setCookie(
            name,
            expiration,
            value,
            this.cookieDomain,
            this.path,
            true,
            "None"
        );
    }
        
    /**
     * Verifica se il cookie di sessione esiste e deve essere aggiornato
     * 
     */
    public shouldUpdateCookies(): boolean {
        if (this.cookieVersion != null) {
            const curVer = this.getCurrentCookieVersion();
            if (curVer != null) {
                return curVer < this.cookieVersion;
            }
            else {
                return true;
            }
        }
        else {
            this.logger.warn("Using SNHubSessionFunctions without cookie versioning");
            return false;
        }
    }
}
